"use client";

import useSWR from "swr";
import type { SubscriptionCheckData } from "./types";
import { SUBSCRIPTION_STATUS_KEY, SWR_CONFIG } from "./constants";

/**
 * Custom hook for checking subscription status with SWR.
 *
 * Accepts a `fetcher` so the data source is injectable — each app
 * provides its own server action (auth resolution differs per partner).
 *
 * @param fetcher - Async function that returns subscription check data
 * @param fallbackData - Server-resolved status shown until the first fetch
 *   lands. SWR only uses it while the cache entry is empty, so it covers a
 *   cold document load; a warm cache wins over a fresher seed.
 * @returns SWR response with subscription check data
 */
export function useSubscriptionCheck(
  fetcher: () => Promise<SubscriptionCheckData>,
  fallbackData?: SubscriptionCheckData
) {
  const { data, error, isLoading, mutate } = useSWR<SubscriptionCheckData>(
    SUBSCRIPTION_STATUS_KEY,
    fetcher,
    { ...SWR_CONFIG, fallbackData }
  );

  return {
    data,
    error,
    isLoading,
    mutate,
    hasActiveSubscription: data?.hasActiveSubscription ?? false,
    subscriptionState: data?.subscriptionState,
    isTrialing: data?.isTrialing ?? false,
  };
}
